import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { t } from "../../../lib/sprache";
import { loadTeam, type MemberRow } from "./data";
import { CHEF_POWERS, RIGHTS, RIGHTS_PRESETS, presetOf } from "./model";
import { NurAdmin, SettingsFrame } from "./parts";
import { EINST } from "./baum";

/**
 * Rollen & Rechte (IA-1 Schritt 3, Zoho-Vorbild „Rollen“): Übersicht, nur lesend. CreatorOffice kennt zwei Rollen (Admin und
 * Manager); was ein Manager sieht und darf, steht je Person. Die Rechte-Vorlagen beim Einladen wirken wie Rollen, deshalb stehen
 * sie hier mit der Zahl der Personen, deren Rechte genau passen. Ändern geht unter Mitglieder und Rechte (gleiche Daten).
 */
export function Rollen() {
  const ws = useStaffWorkspace();
  if (!ws.isChef || ws.isCreator) return <NurAdmin />;
  return <RollenSeite orgId={ws.org.org_id} />;
}

/** Name der Person; ohne Anzeigenamen die Mail in Kleinbuchstaben (die Spaltenköpfe sind sonst in Versalien gesetzt) */
const name = (m: MemberRow) => m.display_name?.trim() || m.email?.toLowerCase() || t("Ohne Namen", "Without name");
const rechteVon = (m: MemberRow) => ({
  sees_all_talents: m.sees_all_talents, can_approve_invoices: m.can_approve_invoices, can_send_invoices: m.can_send_invoices, can_dun: m.can_dun,
  can_confirm_payments: m.can_confirm_payments, can_trigger_payouts: m.can_trigger_payouts, can_sign_contracts: m.can_sign_contracts,
  can_edit_templates: !!m.can_edit_templates, can_edit_contract_text: !!m.can_edit_contract_text, can_view_financials: !!m.can_view_financials,
  can_view_amounts: m.can_view_amounts !== false,
});

function RollenSeite({ orgId }: { orgId: string }) {
  // Gleicher Abfrageschlüssel wie Mitglieder und Rechte: die Seiten zeigen denselben Stand
  const q = useQuery({ queryKey: ["memberships", orgId, "team"], queryFn: () => loadTeam(api, orgId) });
  const rechteGesperrt = useModul("module_rights").gesperrt;
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const aktiv = q.data.members.filter((m) => m.active);
  const admins = aktiv.filter((m) => m.role === "chef_manager");
  const manager = aktiv.filter((m) => m.role !== "chef_manager");
  const vorlage = (m: MemberRow) => presetOf(rechteVon(m));
  const zeilen = [
    { key: "admin", rolle: t("Admin", "Admin"), text: CHEF_POWERS(), zahl: admins.length },
    { key: "manager", rolle: "Manager", text: t("Arbeitet im Tagesgeschäft. Was jede Person sieht und darf, legst du einzeln fest.", "Works in day-to-day business. You set what each person sees and may do individually."), zahl: manager.length },
    ...RIGHTS_PRESETS.map((p) => ({ key: p.key, rolle: `Manager · ${p.label}`, text: p.text, zahl: manager.filter((m) => vorlage(m) === p.key).length, vorlage: true })),
  ];
  return (
    <SettingsFrame section="team" seite="rollen" orgId={orgId} eyebrow="" title={t("Rollen & Rechte", "Roles & permissions")}
      text={t("Wer im Team was sehen und tun darf. Ändern unter Mitglieder und Rechte.", "Who in the team may see and do what. Change under Members and permissions.")}
      actions={<Link className="btn" to={`${EINST}/team#einladen-title`}>{t("Manager einladen", "Invite manager")}</Link>}>
      {rechteGesperrt && <TarifHinweis modul="module_rights" id="rollen-tarif" was={t("Rechte einschränken", "Restricting permissions")} />}
      <section className="panel stack" aria-labelledby="rollen-title">
        <h2 id="rollen-title">{t("Rollen", "Roles")}</h2>
        <div className="tablewrap tk-cardtable tk-rollen">
          <table>
            <caption className="sr-only">{t("Rollen und Rechte-Vorlagen", "Roles and permission templates")}</caption>
            <thead><tr><th scope="col">{t("Rolle", "Role")}</th><th scope="col">{t("Beschreibung", "Description")}</th><th scope="col">{t("Personen", "People")}</th></tr></thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.key} className={"vorlage" in z ? "tk-rolle-vorlage" : undefined}>
                  <td data-label={t("Rolle", "Role")}><strong>{z.rolle}</strong></td>
                  <td data-label={t("Beschreibung", "Description")} className="tk-rolle-text">{z.text}</td>
                  <td data-label={t("Personen", "People")} className="num">{z.zahl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <small>{t("Rechte-Vorlagen sind Vorschläge beim Einladen. Gezählt wird, wessen Rechte genau einer Vorlage entsprechen.", "Permission templates are suggestions when inviting. Counted are people whose permissions match a template exactly.")}</small>
      </section>
      <section className="panel stack" aria-labelledby="rechte-person-title">
        <h2 id="rechte-person-title">{t("Rechte je Person", "Permissions per person")}</h2>
        {manager.length === 0 ? (
          <p>{t("Noch keine Manager im Team. Admins haben immer alle Rechte.", "No managers in the team yet. Admins always have all permissions.")}</p>
        ) : (
          // Zeilen = Rechte, Spalten = Personen: kleine Teams passen so auch am Handy ohne Querscrollen
          <div className="tablewrap tk-rechte-matrix">
            <table>
              <caption className="sr-only">{t("Rechte der Manager", "Permissions of the managers")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("Recht", "Permission")}</th>
                  {manager.map((m) => <th key={m.id} scope="col" className="tk-rechte-person" title={m.email ?? undefined}>{name(m)}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">{t("Vorlage", "Template")}</th>
                  {manager.map((m) => <td key={m.id}>{RIGHTS_PRESETS.find((p) => p.key === vorlage(m))?.label ?? t("Eigene Auswahl", "Custom selection")}</td>)}
                </tr>
                <tr>
                  <th scope="row">{t("Sichtbereich", "Visible scope")}</th>
                  {manager.map((m) => <td key={m.id}>{m.sees_all_talents ? t("alle Creator", "all creators") : t("zugewiesene Creator", "assigned creators")}</td>)}
                </tr>
                {RIGHTS.map((x) => (
                  <tr key={x.key}>
                    <th scope="row">{x.label}</th>
                    {manager.map((m) => (
                      <td key={m.id} className="tk-recht-zelle">
                        {rechteVon(m)[x.key] ? <span className="pill success" aria-label={t("ja", "yes")}>✓</span> : <span className="muted" aria-label={t("nein", "no")}>–</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row"><Link className="btn" to={`${EINST}/team`}>{t("Rechte ändern", "Change permissions")}</Link></div>
      </section>
    </SettingsFrame>
  );
}
